import { useCallback, useEffect, useState } from 'react'
import { useFilterStoreApiOptional } from '@/stores/filter-store-provider'
import type { ExploreFilter, GraphExclusion } from '@/types/graph-filter'
import { createDefaultExclusionGroup, createEmptyGraphExclusion } from '@/types/graph-filter'

export interface GraphExclusionState {
  exclusion: GraphExclusion

  // Entity type management
  hideEntityType: (type: string) => void
  showEntityType: (type: string) => void
  clearHiddenEntityTypes: () => void

  // Entity management
  hideEntity: (id: string, name: string) => void
  showEntity: (id: string) => void
  clearHiddenEntities: () => void

  // Relationship type management
  hideRelationshipType: (type: string) => void
  showRelationshipType: (type: string) => void
  clearHiddenRelationshipTypes: () => void

  // Individual relationship management
  hideRelationship: (id: string, label: string) => void
  showRelationship: (id: string) => void
  clearHiddenRelationships: () => void

  // Group management
  addGroup: () => string // Returns group ID
  removeGroup: (groupId: string) => void
  setGroupOperator: (groupId: string, operator: 'OR' | 'AND') => void

  // Filter management within groups
  addFilterToGroup: (groupId: string, filter: Omit<ExploreFilter, 'id'>) => void
  removeFilterFromGroup: (groupId: string, filterId: string) => void

  // Utility
  clearAll: () => void
}

const STORAGE_KEY_PREFIX = 'graph-exclusion-'
const OLD_FILTER_KEY_PREFIX = 'graph-filter-'

// Migrate old filter format to new exclusion format
function migrateOldFilters(caseId: string): GraphExclusion | null {
  const oldKey = `${OLD_FILTER_KEY_PREFIX}${caseId}`
  const oldData = localStorage.getItem(oldKey)

  if (!oldData) return null

  try {
    const _oldFilter = JSON.parse(oldData)

    // Old filters were inclusion-based, we can't automatically convert to exclusion
    // So we'll just clear them and start fresh
    console.log('Migrating from old filter format to exclusion format - clearing old filters')
    localStorage.removeItem(oldKey)

    return createEmptyGraphExclusion()
  } catch (error) {
    console.error('Failed to migrate old filters:', error)
    return null
  }
}

export function useGraphExclusion(caseId: string): GraphExclusionState {
  const storageKey = `${STORAGE_KEY_PREFIX}${caseId}`

  // Load initial exclusion from localStorage with migration
  const [exclusion, setExclusion] = useState<GraphExclusion>(() => {
    try {
      // Try new format first
      const stored = localStorage.getItem(storageKey)
      if (stored) {
        const parsed = JSON.parse(stored)
        // Migrate old exclusion format - add missing fields
        if (!parsed.hiddenEntityTypes) {
          return {
            ...createEmptyGraphExclusion(),
            ...parsed,
            hiddenEntityTypes: [],
          }
        }
        return parsed
      }

      // Try migrating old format
      const migrated = migrateOldFilters(caseId)
      if (migrated) {
        return migrated
      }

      return createEmptyGraphExclusion()
    } catch {
      return createEmptyGraphExclusion()
    }
  })

  // Persist to localStorage whenever exclusion changes
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(exclusion))
      // Dispatch custom event for same-tab sync between components
      window.dispatchEvent(
        new CustomEvent('graph-exclusion-change', {
          detail: { caseId, exclusion },
        })
      )
    } catch (error) {
      console.error('Failed to persist graph exclusion:', error)
    }
  }, [exclusion, storageKey, caseId])

  // Forward-only sync into the canonical GraphView (filter store) so Save
  // captures the user's hide state. The legacy localStorage-backed exclusion
  // is kept as the source of truth for the exclusion panel UI; the store
  // mirrors the renderable subset (hiddenIds / hiddenTypes / hiddenRelTypes).
  // The store may be unmounted (this hook is also used in `case-graph-explorer`
  // outside InvestigateLayout), so the optional accessor returns null there.
  const storeApi = useFilterStoreApiOptional()
  useEffect(() => {
    if (!storeApi) return
    const state = storeApi.getState()
    const view = state.graphView
    if (!view) return
    const sameArray = (a: string[], b: string[]) =>
      a.length === b.length && a.every((v, i) => v === b[i])
    const desired = {
      hiddenIds: exclusion.hiddenEntityIds,
      hiddenTypes: exclusion.hiddenEntityTypes,
      hiddenRelationshipTypes: exclusion.hiddenRelationshipTypes ?? [],
    }
    if (
      sameArray(view.view.hiddenIds, desired.hiddenIds) &&
      sameArray(view.view.hiddenTypes, desired.hiddenTypes) &&
      sameArray(view.view.hiddenRelationshipTypes, desired.hiddenRelationshipTypes)
    ) {
      return
    }
    storeApi.setState({
      graphView: {
        ...view,
        view: { ...view.view, ...desired },
      },
    })
  }, [exclusion, storeApi])

  // Listen for exclusion changes from other components in the same tab
  useEffect(() => {
    const handleExclusionChange = (
      event: CustomEvent<{ caseId: string; exclusion: GraphExclusion }>
    ) => {
      // Only update if it's for the same case and different from current state
      if (event.detail.caseId === caseId) {
        const newExclusion = event.detail.exclusion
        // Compare by JSON to avoid infinite loops
        setExclusion(prev => {
          if (JSON.stringify(prev) === JSON.stringify(newExclusion)) {
            return prev
          }
          return newExclusion
        })
      }
    }

    window.addEventListener('graph-exclusion-change', handleExclusionChange as EventListener)
    return () => {
      window.removeEventListener('graph-exclusion-change', handleExclusionChange as EventListener)
    }
  }, [caseId])

  // Entity type management
  const hideEntityType = useCallback((type: string) => {
    setExclusion(prev => {
      if (prev.hiddenEntityTypes.includes(type)) return prev
      return {
        ...prev,
        hiddenEntityTypes: [...prev.hiddenEntityTypes, type],
      }
    })
  }, [])

  const showEntityType = useCallback((type: string) => {
    setExclusion(prev => ({
      ...prev,
      hiddenEntityTypes: prev.hiddenEntityTypes.filter(t => t !== type),
    }))
  }, [])

  const clearHiddenEntityTypes = useCallback(() => {
    setExclusion(prev => ({
      ...prev,
      hiddenEntityTypes: [],
    }))
  }, [])

  // Entity management
  const hideEntity = useCallback((id: string, name: string) => {
    setExclusion(prev => {
      // Don't add if already hidden
      if (prev.hiddenEntityIds.includes(id)) return prev

      return {
        ...prev,
        hiddenEntityIds: [...prev.hiddenEntityIds, id],
        hiddenEntityNames: { ...prev.hiddenEntityNames, [id]: name },
      }
    })
  }, [])

  const showEntity = useCallback((id: string) => {
    setExclusion(prev => ({
      ...prev,
      hiddenEntityIds: prev.hiddenEntityIds.filter(eid => eid !== id),
      hiddenEntityNames: Object.fromEntries(
        Object.entries(prev.hiddenEntityNames).filter(([key]) => key !== id)
      ),
    }))
  }, [])

  const clearHiddenEntities = useCallback(() => {
    setExclusion(prev => ({
      ...prev,
      hiddenEntityIds: [],
      hiddenEntityNames: {},
    }))
  }, [])

  // Relationship type management
  const hideRelationshipType = useCallback((type: string) => {
    setExclusion(prev => {
      const hiddenTypes = prev.hiddenRelationshipTypes || []
      if (hiddenTypes.includes(type)) return prev
      return {
        ...prev,
        hiddenRelationshipTypes: [...hiddenTypes, type],
      }
    })
  }, [])

  const showRelationshipType = useCallback((type: string) => {
    setExclusion(prev => ({
      ...prev,
      hiddenRelationshipTypes: (prev.hiddenRelationshipTypes || []).filter(t => t !== type),
    }))
  }, [])

  const clearHiddenRelationshipTypes = useCallback(() => {
    setExclusion(prev => ({
      ...prev,
      hiddenRelationshipTypes: [],
    }))
  }, [])

  // Individual relationship management
  const hideRelationship = useCallback((id: string, label: string) => {
    setExclusion(prev => {
      const hiddenIds = prev.hiddenRelationshipIds || []
      const hiddenNames = prev.hiddenRelationshipNames || {}
      if (hiddenIds.includes(id)) return prev

      return {
        ...prev,
        hiddenRelationshipIds: [...hiddenIds, id],
        hiddenRelationshipNames: { ...hiddenNames, [id]: label },
      }
    })
  }, [])

  const showRelationship = useCallback((id: string) => {
    setExclusion(prev => ({
      ...prev,
      hiddenRelationshipIds: (prev.hiddenRelationshipIds || []).filter(rid => rid !== id),
      hiddenRelationshipNames: Object.fromEntries(
        Object.entries(prev.hiddenRelationshipNames || {}).filter(([key]) => key !== id)
      ),
    }))
  }, [])

  const clearHiddenRelationships = useCallback(() => {
    setExclusion(prev => ({
      ...prev,
      hiddenRelationshipIds: [],
      hiddenRelationshipNames: {},
    }))
  }, [])

  // Group management
  const addGroup = useCallback(() => {
    const newGroup = createDefaultExclusionGroup()
    setExclusion(prev => ({
      ...prev,
      groups: [...prev.groups, newGroup],
    }))
    return newGroup.id
  }, [])

  const removeGroup = useCallback((groupId: string) => {
    setExclusion(prev => ({
      ...prev,
      groups: prev.groups.filter(g => g.id !== groupId),
    }))
  }, [])

  const setGroupOperator = useCallback((groupId: string, operator: 'OR' | 'AND') => {
    setExclusion(prev => ({
      ...prev,
      groups: prev.groups.map(g => (g.id === groupId ? { ...g, operator } : g)),
    }))
  }, [])

  // Filter management within groups
  const addFilterToGroup = useCallback((groupId: string, filterData: Omit<ExploreFilter, 'id'>) => {
    const newFilter: ExploreFilter = {
      ...filterData,
      id: `filter-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    }

    setExclusion(prev => {
      // Auto-create first group if needed
      let groups = prev.groups
      let targetGroupId = groupId

      if (groups.length === 0) {
        const newGroup = createDefaultExclusionGroup()
        groups = [newGroup]
        targetGroupId = newGroup.id
      }

      return {
        ...prev,
        groups: groups.map(g =>
          g.id === targetGroupId ? { ...g, filters: [...g.filters, newFilter] } : g
        ),
      }
    })
  }, [])

  const removeFilterFromGroup = useCallback((groupId: string, filterId: string) => {
    setExclusion(prev => ({
      ...prev,
      groups: prev.groups.map(g =>
        g.id === groupId ? { ...g, filters: g.filters.filter(f => f.id !== filterId) } : g
      ),
    }))
  }, [])

  // Utility
  const clearAll = useCallback(() => {
    setExclusion(createEmptyGraphExclusion())
  }, [])

  return {
    exclusion,
    hideEntityType,
    showEntityType,
    clearHiddenEntityTypes,
    hideEntity,
    showEntity,
    clearHiddenEntities,
    hideRelationshipType,
    showRelationshipType,
    clearHiddenRelationshipTypes,
    hideRelationship,
    showRelationship,
    clearHiddenRelationships,
    addGroup,
    removeGroup,
    setGroupOperator,
    addFilterToGroup,
    removeFilterFromGroup,
    clearAll,
  }
}
